import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import { LoyaltyCurrency, NavLink, PointsSummary } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  giftCardsPageList,
  myProfilePageList,
  offersPageList,
  PageUrlSegment,
  redeemCashPageList,
  transferProgramsPageList,
} from '../../../types/bnz-page-url-segment.type';
import { getMainUrlSegment } from '../../../utils/bnz-page-discovery.utils';
import { BnzNavLinksService } from '../../services/bnz-nav-links.service';
import { BNZ_BANKWIDE_UPDATES_ENABLED } from '../../tokens/features.tokens';

type MenuSection = 'redeem' | 'my-account' | 'home';
type UserAction = 'login' | 'logout' | 'enroll';

@Component({
  selector: 'bnz-side-nav-content',
  templateUrl: './side-nav-content.component.html',
  styleUrls: ['./side-nav-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BnzSideNavContentComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-bnz-side-nav-content') componentClass = true;

  @Input() isSideNavOpen: boolean;
  @Input() sideNavOpen: Observable<void>;
  @Output() sideNavClose: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  userAction: EventEmitter<UserAction> = new EventEmitter<UserAction>();
  currentOpenMenu: MenuSection | null;
  currentUrlSegment: PageUrlSegment;

  pointsSummary$: Observable<PointsSummary> =
    this.pointsSummaryFacade.pointsSummary$.pipe(filter(isNotNull));
  loyaltyCurrency$: Observable<LoyaltyCurrency> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );

  pointsInformation$: Observable<[PointsSummary, LoyaltyCurrency] | null> =
    combineLatest([this.pointsSummary$, this.loyaltyCurrency$]).pipe(
      filter((values) => values.some(isNotNull)),
    );
  isLoggedIn$: Observable<boolean> = this.authFacade.isLoggedIn$;
  isBnzBankwideUpdatesEnabled = inject(BNZ_BANKWIDE_UPDATES_ENABLED);

  private destroy$ = new Subject();

  constructor(
    private router: Router,
    public navlinksService: BnzNavLinksService,
    private authFacade: AuthFacade,
    private pointsSummaryFacade: PointsSummaryFacade,
    private loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    private cdRef: ChangeDetectorRef,
    private windowRefService: WindowRefService,
  ) {}

  get offerText(): string {
    return this.isBnzBankwideUpdatesEnabled
      ? 'wl.bnz.header.perks'
      : 'wl.bnz.header.offers';
  }

  get isFlybuysLinkActive(): boolean {
    return (
      !this.router.url.includes('rewards_programs') &&
      this.router.url.includes('flybuys')
    );
  }

  isActivatedOffersMenuItem(): boolean {
    return offersPageList.includes(this.currentUrlSegment);
  }

  isActivatedChildMenuItem(pageUrlSegment: string): boolean {
    if (
      this.router.url.includes('transfer') &&
      this.router.url.includes('flybuys')
    ) {
      return false;
    }

    switch (pageUrlSegment) {
      case PageUrlSegment.GIFTCARD_LIST: {
        return giftCardsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.TRANSFER_PROGRAMS: {
        if (this.router.url.includes('flybuys')) {
          return false;
        }
        return transferProgramsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.REDEEMCASH_DETAILS: {
        return redeemCashPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.POINTS_TRANSACTIONS: {
        return this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS;
      }
      case PageUrlSegment.HOMEPAGE: {
        return this.currentUrlSegment === PageUrlSegment.HOMEPAGE;
      }
      case PageUrlSegment.REWARDS_PROGRAMS: {
        return this.currentUrlSegment === PageUrlSegment.REWARDS_PROGRAMS;
      }
      case PageUrlSegment.CONTACT_US: {
        return this.currentUrlSegment === PageUrlSegment.CONTACT_US;
      }
      case PageUrlSegment.FAQ: {
        return this.currentUrlSegment === PageUrlSegment.FAQ;
      }
      case PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT: {
        return (
          this.currentUrlSegment ===
          PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT
        );
      }
      case PageUrlSegment.TERMS: {
        return this.currentUrlSegment === PageUrlSegment.TERMS;
      }
      case PageUrlSegment.AUTO_REDEMPTION_SETTINGS: {
        return (
          this.currentUrlSegment === PageUrlSegment.AUTO_REDEMPTION_SETTINGS
        );
      }
      case PageUrlSegment.SHARE_POINTS: {
        return this.currentUrlSegment === PageUrlSegment.SHARE_POINTS;
      }
    }
    return false;
  }

  ngOnInit(): void {
    this.sideNavOpen.subscribe(() => {
      this.cdRef.detectChanges();
    });

    // We have to reinitialize outside the subscription to handle the scenarios
    // related to page reloads and redirections
    this.currentUrlSegment = getMainUrlSegment(this.router.url);
    this.currentOpenMenu = this.initializeCurrentOpenMenu();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrlSegment = getMainUrlSegment(event.urlAfterRedirects);
        this.currentOpenMenu = this.initializeCurrentOpenMenu();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  isExternalNavLink(navLink: NavLink): boolean {
    const navLinkString = navLink.route.toString();
    return (
      navLinkString.startsWith('http://') ||
      navLinkString.startsWith('https://')
    );
  }

  emitSideNavClose(): void {
    this.sideNavClose.emit();
  }

  getExpandedStatus(menuType: string): boolean {
    return this.currentOpenMenu === menuType;
  }

  toggleMenu(menuSection: MenuSection): void {
    if (this.currentOpenMenu !== menuSection) {
      this.currentOpenMenu = menuSection;
      this.currentUrlSegment = getMainUrlSegment(
        this.windowRefService.nativeWindow.location.pathname,
      );
    } else {
      this.currentOpenMenu = null;
    }
  }

  emitUserAction(action: UserAction): void {
    this.userAction.emit(action);
  }

  private initializeCurrentOpenMenu(): MenuSection {
    const redeemPageList = [
      ...giftCardsPageList,
      ...redeemCashPageList,
      ...transferProgramsPageList,
    ];

    if (redeemPageList.includes(this.currentUrlSegment)) {
      return 'redeem';
    } else if (myProfilePageList.includes(this.currentUrlSegment)) {
      return 'my-account';
    } else {
      return 'home';
    }
  }
}
